// Farm time (findings #85): one rule for days and times across the web app,
// which is used from more than one time zone.
//
//   A farm's day — and the time shown for a farm record — is the farm's time
//   zone (`Farm.metadata.timezone`), never UTC and never the viewer's browser.
//
// Stored shapes (all kept as they are; nothing is migrated):
//   - real instant       "2026-09-29T13:45:00.000Z"  Crew captures, web Agua from
//                         this PR on → shown / bucketed in the farm's zone
//   - calendar day       "2026-09-29" or the UTC-midnight sentinel
//                         "2026-09-29T00:00:00Z" (web tables, imports) → that day,
//                         never shifted (west of UTC it used to become the 28th)
//   - wall clock         "2026-09-29T08:30" (datetime-local, no zone) → typed
//                         as local time; its day is its date part
//
// The farm's zone comes from the farm itself, or from a registry filled when
// farms are read from the device store, so helpers that only see a record's
// `farmId` can still use the right zone. Without a known farm the "day zone"
// is the one zone all registered farms share, else the browser's.

import { getBrowserTimeZone, getTimeZoneFromFarm, isValidTimeZone } from './timeBucketing'

type FarmLike = { id?: string; metadata?: unknown } | null | undefined

const farmZones = new Map<string, string>()

/** Remember each farm's time zone (called whenever farms are read). */
export const registerFarmTimeZones = (farms: FarmLike[]): void => {
  for (const farm of farms) {
    if (!farm?.id) continue
    const zone = getTimeZoneFromFarm(farm)
    if (zone && isValidTimeZone(zone)) farmZones.set(farm.id, zone)
    else farmZones.delete(farm.id)
  }
}

/** Test helper. */
export const clearFarmTimeZones = (): void => farmZones.clear()

/** The zone used when no farm is known: the farms' shared zone, else the browser's. */
export const defaultDayTimeZone = (): string => {
  const zones = new Set(farmZones.values())
  if (zones.size === 1) return [...zones][0]
  const browser = getBrowserTimeZone()
  return isValidTimeZone(browser) ? browser : 'UTC'
}

/** The farm's time zone (farm object or id), falling back to `defaultDayTimeZone()`. */
export const farmTimeZone = (farm?: FarmLike | string | null): string => {
  if (typeof farm === 'string') return farmZones.get(farm) ?? defaultDayTimeZone()
  const own = getTimeZoneFromFarm(farm)
  if (own && isValidTimeZone(own)) return own
  if (farm?.id && farmZones.has(farm.id)) return farmZones.get(farm.id) as string
  return defaultDayTimeZone()
}

const CALENDAR_DAY = /^(\d{4}-\d{2}-\d{2})(T00:00(:00(\.0+)?)?Z)?$/
const WALL_CLOCK = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/

/** A bare date or the UTC-midnight "calendar day" sentinel. */
export const isCalendarDayValue = (raw: unknown): boolean => (
  typeof raw === 'string' && CALENDAR_DAY.test(raw.trim())
)

/** A datetime-local value with no zone (typed wall-clock time). */
export const isWallClockValue = (raw: unknown): boolean => (
  typeof raw === 'string' && WALL_CLOCK.test(raw.trim())
)

type ZonedParts = { year: string; month: string; day: string; hour: string; minute: string }

const zonedParts = (date: Date, timeZone: string): ZonedParts | null => {
  if (Number.isNaN(date.getTime())) return null
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(date)
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? ''
  const out = { year: get('year'), month: get('month'), day: get('day'), hour: get('hour'), minute: get('minute') }
  return out.year && out.month && out.day ? out : null
}

/**
 * The farm day (YYYY-MM-DD) a stored value belongs to: calendar days and
 * wall-clock values keep their date, instants are converted to `timeZone`.
 */
export const farmDayKey = (raw: unknown, timeZone: string = defaultDayTimeZone()): string | null => {
  if (raw instanceof Date) {
    const parts = zonedParts(raw, timeZone)
    return parts ? `${parts.year}-${parts.month}-${parts.day}` : null
  }
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  if (!trimmed) return null
  const day = trimmed.match(CALENDAR_DAY)?.[1] ?? trimmed.match(WALL_CLOCK)?.[1]
  if (day) return day
  const parts = zonedParts(new Date(trimmed), timeZone)
  return parts ? `${parts.year}-${parts.month}-${parts.day}` : null
}

/** "Today" on the farm (YYYY-MM-DD). */
export const farmToday = (timeZone: string = defaultDayTimeZone(), now: Date = new Date()): string => (
  farmDayKey(now, timeZone) ?? now.toISOString().slice(0, 10)
)

/** The farm day `daysAgo` days before today. */
export const farmDayOffset = (days: number, timeZone: string = defaultDayTimeZone(), now: Date = new Date()): string => {
  const today = farmToday(timeZone, now)
  return new Date(Date.parse(`${today}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10)
}

/**
 * A typed time that converts to exactly UTC midnight (19:00 in Lima, 21:00 in
 * Buenos Aires) would read back as the calendar-day sentinel — the day after.
 * Such an instant is stored one second later: invisible at minute precision,
 * and it keeps the reading on its own day (findings #85).
 */
export const avoidCalendarDaySentinel = (iso: string): string => (
  /T00:00:00(\.0+)?Z$/.test(iso) ? new Date(Date.parse(iso) + 1000).toISOString() : iso
)

/** Offset of `timeZone` from UTC at `instant`, in minutes (Lima → −300). */
const zoneOffsetMinutes = (instant: Date, timeZone: string): number => {
  const parts = zonedParts(instant, timeZone)
  if (!parts) return 0
  const asUtc = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute))
  const truncated = Math.floor(instant.getTime() / 60_000) * 60_000
  return Math.round((asUtc - truncated) / 60_000)
}

/**
 * The instant for a wall-clock time on the farm ("2026-09-29T08:30" in Lima →
 * "2026-09-29T13:30:00.000Z"). Returns null for an unreadable value.
 */
export const farmWallClockToInstant = (value: string, timeZone: string = defaultDayTimeZone()): string | null => {
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/)
  if (!match) return null
  const [, y, mo, d, h = '00', mi = '00'] = match
  const guess = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi))
  if (Number.isNaN(guess)) return null
  // Two passes settle the offset across a daylight-saving change.
  let instant = guess - zoneOffsetMinutes(new Date(guess), timeZone) * 60_000
  instant = guess - zoneOffsetMinutes(new Date(instant), timeZone) * 60_000
  return avoidCalendarDaySentinel(new Date(instant).toISOString())
}

/**
 * The value for an `<input type="datetime-local">` showing a stored value in
 * farm time. A calendar day shows as that day at 00:00 (it has no time).
 */
export const farmDateTimeInputValue = (raw: unknown, timeZone: string = defaultDayTimeZone()): string => {
  if (typeof raw !== 'string' || !raw.trim()) return ''
  const trimmed = raw.trim()
  const day = trimmed.match(CALENDAR_DAY)?.[1]
  if (day) return `${day}T00:00`
  if (WALL_CLOCK.test(trimmed)) return trimmed.slice(0, 16)
  const parts = zonedParts(new Date(trimmed), timeZone)
  return parts ? `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}` : ''
}

/**
 * A stored value for display in farm time: a calendar day as a date only, an
 * instant as date and time in the farm's zone.
 */
export const formatFarmDateTime = (
  raw: unknown,
  timeZone: string = defaultDayTimeZone(),
  options: { locale?: string; dateOnly?: boolean } = {}
): string => {
  if (typeof raw !== 'string' || !raw.trim()) return ''
  const trimmed = raw.trim()
  const day = trimmed.match(CALENDAR_DAY)?.[1] ?? (options.dateOnly ? trimmed.match(WALL_CLOCK)?.[1] : undefined)
  if (day) {
    return new Date(`${day}T12:00:00Z`).toLocaleDateString(options.locale, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })
  }
  if (WALL_CLOCK.test(trimmed)) {
    const [datePart, timePart] = trimmed.split('T')
    return `${new Date(`${datePart}T12:00:00Z`).toLocaleDateString(options.locale, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })} ${timePart.slice(0, 5)}`
  }
  const date = new Date(trimmed)
  if (Number.isNaN(date.getTime())) return trimmed
  return options.dateOnly
    ? date.toLocaleDateString(options.locale, { year: 'numeric', month: 'short', day: 'numeric', timeZone })
    : date.toLocaleString(options.locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone })
}
